/**
 * VoiceAgentProvider — v2 context for the QNNEAL Voice Agent.
 *
 * Manages: immersive overlay state, mute, voice state, action labels,
 * and integration with the AutomationContext for UI control.
 */
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from "react";

const VoiceAgentContext = createContext(null);

export function VoiceAgentProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isImmersive, setIsImmersive] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [voiceState, setVoiceState] = useState("idle");
  const [actionLabel, setActionLabel] = useState("");
  const [audioLevel, setAudioLevel] = useState(0);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const animRef = useRef(null);
  const streamRef = useRef(null);

  // Start audio capture for orb reactivity
  const startAudioCapture = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const tick = () => {
        analyser.getByteFrequencyData(dataArray);
        const avg = dataArray.reduce((sum, v) => sum + v, 0) / dataArray.length / 255;
        setAudioLevel(avg);
        animRef.current = requestAnimationFrame(tick);
      };
      animRef.current = requestAnimationFrame(tick);
    } catch {
      // Mic permission denied — orb still animates but without audio reactivity
    }
  }, []);

  const stopAudioCapture = useCallback(() => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    if (audioCtxRef.current) audioCtxRef.current.close().catch(() => {});
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    audioCtxRef.current = null;
    analyserRef.current = null;
    streamRef.current = null;
    setAudioLevel(0);
  }, []);

  const openImmersive = useCallback(() => {
    setIsImmersive(true);
    setIsOpen(true);
    setVoiceState("listening");
    startAudioCapture();
  }, [startAudioCapture]);

  const closeImmersive = useCallback(() => {
    setIsImmersive(false);
    setVoiceState("idle");
    setActionLabel("");
    stopAudioCapture();
  }, [stopAudioCapture]);

  const toggleMute = useCallback(() => {
    setIsMuted(prev => {
      const next = !prev;
      if (streamRef.current) {
        streamRef.current.getAudioTracks().forEach(t => { t.enabled = !next; });
      }
      return next;
    });
  }, []);

  // Expose action label setter on window for AutomationContext to call
  useEffect(() => {
    window.__qnnealVoiceActionLabel = (label) => setActionLabel(label);
    return () => { delete window.__qnnealVoiceActionLabel; };
  }, []);

  // Esc key to close immersive
  useEffect(() => {
    if (!isImmersive) return;
    const handler = (e) => { if (e.key === "Escape") closeImmersive(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isImmersive, closeImmersive]);

  const value = {
    isAvailable: true,
    isOpen,
    isImmersive,
    isMuted,
    voiceState,
    actionLabel,
    audioLevel,
    open: useCallback(() => setIsOpen(true), []),
    close: useCallback(() => { setIsOpen(false); closeImmersive(); }, [closeImmersive]),
    toggle: useCallback(() => setIsOpen(p => !p), []),
    openImmersive,
    closeImmersive,
    toggleMute,
    setVoiceState,
    setActionLabel,
    triggerWithContext: useCallback((context) => {
      openImmersive();
    }, [openImmersive]),
  };

  return (
    <VoiceAgentContext.Provider value={value}>
      {children}
    </VoiceAgentContext.Provider>
  );
}

export function useVoiceAgent() {
  const ctx = useContext(VoiceAgentContext);
  if (!ctx) {
    return {
      isAvailable: false, isOpen: false, isImmersive: false, isMuted: false,
      voiceState: "idle", actionLabel: "", audioLevel: 0,
      open: () => {}, close: () => {}, toggle: () => {},
      openImmersive: () => {}, closeImmersive: () => {}, toggleMute: () => {},
      setVoiceState: () => {}, setActionLabel: () => {}, triggerWithContext: () => {},
    };
  }
  return ctx;
}
